import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, addDoc, collection, serverTimestamp, runTransaction } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { duplicateInvoice } from '../utils/invoiceUtils';
import PaymentModal from '../components/PaymentModal';
import ConfirmationModal from '../components/ConfirmationModal';
import '../App.css';

export default function ViewInvoice({ userRole }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const printRef = useRef();
  
  // Modal states
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [receiptId, setReceiptId] = useState(null);

  // Format currency in ZAR
  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2
    }).format(amount);
  }, []);

  // Add calculateSubtotal function
  const calculateSubtotal = useCallback(() => {
    if (!invoice || !invoice.items) return 0;
    
    return invoice.items.reduce((total, item) => {
      return total + (item.quantity * item.price);
    }, 0);
  }, [invoice]);

  // Wrap formatDate in useCallback
  const formatDate = useCallback((dateInput) => {
    if (!dateInput) return 'N/A';
    
    try {
      // Handle Firestore timestamps (which have toDate method)
      if (dateInput && typeof dateInput.toDate === 'function') {
        return dateInput.toDate().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      
      // Handle serialized Firestore timestamps (from JSON)
      if (typeof dateInput === 'object' && dateInput.seconds) {
        const date = new Date(dateInput.seconds * 1000);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      
      // Handle regular Date objects or date strings
      const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
      
      // Check if date is valid before formatting
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error, dateInput);
      return 'Invalid Date';
    }
  }, []);

  // Memoize the PDF generation function to prevent unnecessary re-renders
  const generatePdfPreview = useCallback((invoiceData) => {
    if (!invoiceData) return;
    
    try {
      // Use a web worker for PDF generation to avoid blocking the main thread
      const worker = new Worker(new URL('../workers/pdfGenerator.js', import.meta.url));
      
      worker.onmessage = (e) => {
        if (e.data.type === 'preview') {
          setPdfPreview(e.data.preview);
        } else if (e.data.type === 'error') {
          console.error('PDF generation error:', e.data.error);
          setError('Error generating PDF preview. Please try again later.');
        }
      };
      
      worker.onerror = (error) => {
        console.error('Worker error:', error);
        setError('Error initializing PDF generator. Please try again later.');
      };
      
      // Create a deep copy of the invoice data to avoid issues with Firestore objects
      const safeInvoiceData = JSON.parse(JSON.stringify(invoiceData));
      
      // Ensure dates are properly formatted for the worker
      if (safeInvoiceData.dueDate) {
        safeInvoiceData.dueDateFormatted = formatDate(safeInvoiceData.dueDate);
      }
      
      if (safeInvoiceData.invoiceDate) {
        safeInvoiceData.invoiceDateFormatted = formatDate(safeInvoiceData.invoiceDate);
      }
      
      if (safeInvoiceData.createdAt) {
        safeInvoiceData.createdAtFormatted = formatDate(safeInvoiceData.createdAt);
      }
      
      worker.postMessage({
        action: 'generatePreview',
        invoiceData: safeInvoiceData
      });
      
      return () => {
        worker.terminate();
      };
    } catch (error) {
      console.error('Error setting up PDF worker:', error);
      setError('Failed to initialize PDF generator');
    }
  }, [formatDate]);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const invoiceRef = doc(db, 'invoices', id);
        const invoiceSnap = await getDoc(invoiceRef);
        
        if (invoiceSnap.exists()) {
          const data = {
            id: invoiceSnap.id,
            ...invoiceSnap.data()
          };
          setInvoice(data);
          
          // Generate PDF preview after invoice is loaded
          if (data) {
            generatePdfPreview(data);
          }
        } else {
          setError('Invoice not found');
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching invoice:', error);
        setError('Error loading invoice');
        setLoading(false);
      }
    };
    
    fetchInvoice();
  }, [id, generatePdfPreview]); // Added generatePdfPreview to dependencies

  // Add this function to handle status changes
  const handleStatusChange = useCallback((newStatus) => {
    if (!invoice) return;
    
    try {
      const invoiceRef = doc(db, 'invoices', id);
      
      if (newStatus === 'paid') {
        // For 'paid' status, open payment modal instead of directly changing status
        setIsPaymentModalOpen(true);
      } else {
        // For other statuses, update directly
        updateDoc(invoiceRef, { status: newStatus });
        setInvoice((prev) => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Error updating invoice status:', error);
    }
  }, [id, invoice]);

  const handlePayment = useCallback(async (paymentData) => {
    try {
      const { amount: paymentAmount, method: paymentMethod } = paymentData;
      
      // Validate payment amount
      if (isNaN(paymentAmount) || paymentAmount <= 0) {
        alert('Please enter a valid payment amount');
        return;
      }
      
      // Calculate amount paid and remaining balance
      const previouslyPaid = invoice.amountPaid || 0;
      const totalPaid = previouslyPaid + paymentAmount;
      const remainingBalance = invoice.total - totalPaid;
      
      // Determine status based on payment amount
      let newStatus;
      
      // If remaining balance is zero or negative (overpayment), mark as fully paid
      if (remainingBalance <= 0) {
        newStatus = 'paid';
      } 
      // If it's a partial payment but not the full amount
      else if (totalPaid < invoice.total) {
        newStatus = 'partial';
      }
      // Fallback (should not happen)
      else {
        newStatus = 'paid';
      }
      
      // Get next receipt number from counter
      let receiptNumber;
      await runTransaction(db, async (transaction) => {
        const counterRef = doc(db, 'counters', 'receipts');
        const counterDoc = await transaction.get(counterRef);
        
        let nextNumber = 1;
        if (counterDoc.exists()) {
          nextNumber = counterDoc.data().count + 1;
        }
        
        transaction.set(counterRef, { count: nextNumber });
        receiptNumber = `RCT-${nextNumber.toString().padStart(6, '0')}`;
      });
      
      // Create a payment record
      const paymentRecord = {
        amount: paymentAmount,
        date: new Date(),
        method: paymentMethod,
        receiptNumber: receiptNumber
      };
      
      // Get existing payment history or create a new array
      const paymentHistory = invoice.paymentHistory || [];
      
      // Add the new payment to history
      paymentHistory.push(paymentRecord);
      
      // Update invoice with new status, payment info, and payment history
      const invoiceRef = doc(db, 'invoices', id);
      await updateDoc(invoiceRef, {
        status: newStatus,
        amountPaid: totalPaid,
        remainingBalance: remainingBalance,
        paymentHistory: paymentHistory
      });
      
      setInvoice((prevInvoice) => ({
        ...prevInvoice,
        status: newStatus,
        amountPaid: totalPaid,
        remainingBalance: remainingBalance,
        paymentHistory: paymentHistory
      }));
      
      // If payment was made, generate a receipt
      if (paymentAmount > 0) {
        // Make sure we have the current user ID
        if (!auth.currentUser) {
          console.error('No authenticated user found');
          return;
        }
        
        // Determine if this is a partial payment (for receipt purposes)
        const isPartialPayment = newStatus === 'partial';
        
        const receiptData = {
          clientName: invoice.clientName || invoice.customerName,
          clientEmail: invoice.clientEmail || invoice.customerEmail,
          clientAddress: invoice.clientAddress || invoice.customerAddress,
          clientPhone: invoice.clientPhone || invoice.customerPhone,
          companyName: invoice.companyName || '',
          companyEmail: invoice.companyEmail || '',
          companyAddress: invoice.companyAddress || '',
          companyPhone: invoice.companyPhone || '',
          paymentDate: new Date(),
          paymentMethod: paymentMethod,
          invoiceNumber: invoice.invoiceNumber || invoice.invoiceNo,
          amount: paymentAmount,
          totalInvoiceAmount: invoice.total,
          remainingBalance: remainingBalance,
          isPartialPayment: isPartialPayment,
          notes: `Payment for invoice #${invoice.invoiceNumber || invoice.invoiceNo}${isPartialPayment ? ' (Partial Payment)' : ''}`,
          receiptNumber: paymentRecord.receiptNumber,
          userId: auth.currentUser.uid,
          createdAt: serverTimestamp(),
          logoBase64: invoice.logoBase64
        };
        
        console.log('Creating receipt with data:', receiptData);
        
        // Add receipt to Firestore
        try {
          const docRef = await addDoc(collection(db, 'receipts'), receiptData);
          console.log('Receipt created with ID:', docRef.id);
          
          // Store receipt ID and show confirmation modal
          setReceiptId(docRef.id);
          setIsReceiptModalOpen(true);
        } catch (receiptError) {
          console.error('Error creating receipt:', receiptError);
        }
      }
    } catch (error) {
      console.error('Error processing payment:', error);
    }
  }, [id, invoice]); // Removed formatCurrency from dependencies

  // Function to handle viewing the receipt
  const handleViewReceipt = useCallback(() => {
    if (receiptId) {
      navigate(`/receipt/${receiptId}`);
    }
  }, [receiptId, navigate]);

  // const handlePrint = useCallback(() => {
  //   window.print();
  // }, []);

  const handleDownloadPDF = useCallback(() => {
    if (!invoice) return;
    
    // Show loading indicator
    setDownloading(true);
    
    try {
      // Use a web worker for PDF generation
      const worker = new Worker(new URL('../workers/pdfGenerator.js', import.meta.url));
      
      worker.onmessage = (e) => {
        if (e.data.type === 'download') {
          // Create a download link
          const link = document.createElement('a');
          link.href = e.data.pdfUrl;
          link.download = `invoice_${invoice.invoiceNo || invoice.invoiceNumber}_${Date.now()}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setDownloading(false);
        } else if (e.data.type === 'error') {
          console.error('PDF download error:', e.data.error);
          setError('Error generating PDF. Please try again later.');
          setDownloading(false);
        }
      };
      
      worker.onerror = (error) => {
        console.error('Worker error:', error);
        setError('Error initializing PDF generator. Please try again later.');
        setDownloading(false);
      };
      
      // Create a deep copy of the invoice data to avoid issues with Firestore objects
      const safeInvoiceData = JSON.parse(JSON.stringify(invoice));
      
      // Ensure dates are properly formatted for the worker
      if (safeInvoiceData.dueDate) {
        // Store the formatted date string
        safeInvoiceData.dueDateFormatted = formatDate(safeInvoiceData.dueDate);
        // console.log('Due date formatted:', safeInvoiceData.dueDateFormatted);
      }
      
      if (safeInvoiceData.invoiceDate) {
        // Store the formatted date string
        safeInvoiceData.invoiceDateFormatted = formatDate(safeInvoiceData.invoiceDate);
      }
      
      if (safeInvoiceData.createdAt) {
        // Store the formatted date string
        safeInvoiceData.createdAtFormatted = formatDate(safeInvoiceData.createdAt);
      }
      
      worker.postMessage({
        action: 'generateDownload',
        invoiceData: safeInvoiceData
      });
    } catch (error) {
      console.error('Error setting up PDF worker:', error);
      setError('Failed to initialize PDF generator');
      setDownloading(false);
    }
  }, [invoice, formatDate]);

  const handleDuplicate = useCallback(async () => {
    if (!invoice || duplicating) return;
    setDuplicating(true);
    try {
      const result = await duplicateInvoice(invoice);
      navigate(`/invoice/${result.id}`);
    } catch (error) {
      console.error('Error duplicating invoice:', error);
      alert('Failed to duplicate invoice. Please try again.');
      setDuplicating(false);
    }
  }, [invoice, duplicating, navigate]);

  const handleEmailInvoice = useCallback(() => {
    if (!invoice) return;
    const email = invoice.clientEmail || invoice.customerEmail;
    if (!email) {
      alert('This invoice has no client email address on file.');
      return;
    }
    const number = invoice.invoiceNo || invoice.invoiceNumber;
    const amountDue = invoice.status === 'partial'
      ? invoice.remainingBalance
      : invoice.status === 'paid'
        ? 0
        : invoice.total;
    const subject = `Invoice ${number} from ${invoice.companyName || 'our company'}`;
    const body = [
      `Hi ${invoice.clientName || invoice.customerName || 'there'},`,
      '',
      `Please find your invoice ${number} from ${invoice.companyName || 'our company'}.`,
      invoice.dueDate ? `Due date: ${formatDate(invoice.dueDate)}` : '',
      amountDue > 0 ? `Amount due: ${formatCurrency(amountDue)}` : '',
      '',
      'You can view and download the invoice from the invoice management system.',
      '',
      'Thank you for your business!'
    ].filter(Boolean).join('\n');
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [invoice, formatDate, formatCurrency]);

  if (loading) return (
    <div className="invoice-container">
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading invoice...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="invoice-container">
      <div className="error-container">
        <p className="error-message">{error}</p>
      </div>
    </div>
  );

  if (!invoice) return null;

  return (
    <div className="invoice-container">
      <div className="invoice-grid">
        <div className="invoice-details">
          <div className="invoice-card" ref={printRef}>
            {/* Invoice header with actions */}
            <div className="invoice-header">
              <div>
                <h1 className="invoice-title">Invoice #{invoice.invoiceNo || invoice.invoiceNumber}</h1>
                <p className="invoice-date">
                  {formatDate(invoice.invoiceDate || invoice.createdAt?.toDate())}
                </p>
                
                {/* Status badge */}
                {invoice.status && (
                  <span className={`status-badge ${
                    invoice.status === 'paid' 
                      ? 'status-paid' 
                      : invoice.status === 'pending' 
                      ? 'status-pending' 
                      : invoice.status === 'partial'
                      ? 'status-partial'
                      : 'status-overdue'
                  }`}>
                    <span className={`status-badge-dot ${
                      invoice.status === 'paid' 
                        ? 'status-paid-dot' 
                        : invoice.status === 'pending' 
                        ? 'status-pending-dot' 
                        : invoice.status === 'partial'
                        ? 'status-partial-dot'
                        : 'status-overdue-dot'
                    }`}></span>
                    {invoice.status === 'partial' ? 'Partially Paid' : invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                  </span>
                )}
              </div>
              <div className="invoice-actions print:hidden">
                <button 
                  onClick={handleDownloadPDF}
                  className="btn btn-primary"
                  disabled={downloading}
                >
                  {downloading ? (
                    <>
                      <div className="loading-spinner"></div>
                      Downloading...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="btn-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download PDF
                    </>
                  )}
                </button>
                <Link 
                  to={`/edit-invoice/${id}`}
                  className="btn btn-secondary"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="btn-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </Link>
                <button 
                  onClick={handleEmailInvoice}
                  className="btn btn-secondary"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="btn-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Email
                </button>
                <button 
                  onClick={handleDuplicate}
                  disabled={duplicating}
                  className="btn btn-secondary"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="btn-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                  </svg>
                  {duplicating ? 'Copying...' : 'Copy'}
                </button>
                <button 
                  onClick={() => navigate('/invoices')}
                  className="btn btn-secondary"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="btn-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to Invoices
                </button>
              </div>
            </div>
            
            {/* Status change buttons */}
            {invoice && invoice.status && (
              <div className="mb-8 print:hidden">
                {invoice.status !== 'paid' && (
                  <button 
                    onClick={() => setIsPaymentModalOpen(true)}
                    className="btn btn-success mr-3"
                  >
                    {invoice.status === 'partial' ? 'Add Payment' : 'Mark as Paid'}
                  </button>
                )}
                {invoice.status !== 'pending' && invoice.status !== 'paid' && (
                  <button 
                    onClick={() => {
                      const invoiceRef = doc(db, 'invoices', id);
                      updateDoc(invoiceRef, { status: 'pending' });
                      setInvoice((prev) => ({ ...prev, status: 'pending' }));
                    }}
                    className="btn btn-warning"
                  >
                    Mark as Pending
                  </button>
                )}
              </div>
            )}
            
            <div className="invoice-grid">
              {/* Company Information */}
              <div className="invoice-section">
                <h2 className="invoice-section-title">From</h2>
                <p className="font-semibold">{invoice.companyName || 'Your Company'}</p>
                <p>{invoice.companyAddress || 'Company Address'}</p>
                <p>{invoice.companyEmail || 'company@example.com'}</p>
                <p>{invoice.companyPhone || '(123) 456-7890'}</p>
                {invoice.vatRate > 0 && invoice.taxRegNo && (
                  <p>VAT No: {invoice.taxRegNo}</p>
                )}
              </div>
              
              {/* Customer Information */}
              <div className="invoice-section">
                <h2 className="invoice-section-title">Bill To</h2>
                <p className="font-semibold">{invoice.customerName || invoice.clientName || 'Customer Name'}</p>
                <p>{invoice.customerAddress || invoice.clientAddress || 'Customer Address'}</p>
                <p>{invoice.customerEmail || invoice.clientEmail || 'customer@example.com'}</p>
                <p>{invoice.customerPhone || invoice.clientPhone || 'Customer Phone'}</p>
              </div>
            </div>
            
            {/* Invoice Details */}
            <div className="invoice-details">
              <h2 className="invoice-section-title">Invoice Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="invoice-detail-row">
                    <span className="invoice-detail-label">Invoice Number:</span> 
                    {invoice.invoiceNo || invoice.invoiceNumber}
                  </p>
                  <p className="invoice-detail-row">
                    <span className="invoice-detail-label">Invoice Date:</span> 
                    {formatDate(invoice.invoiceDate)}
                  </p>
                  {invoice.dueDate && (
                    <p className="invoice-detail-row">
                      <span className="invoice-detail-label">Due Date:</span> 
                      {formatDate(invoice.dueDate)}
                    </p>
                  )}
                </div>
                <div>
                  {invoice.createdAt && (
                    <p className="invoice-detail-row">
                      <span className="invoice-detail-label">Created:</span> 
                      {invoice.createdAt.toDate ? formatDate(invoice.createdAt.toDate()) : 'N/A'}
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Call to action for partial payments */}
            {invoice.status === 'partial' && (
              <div className="bg-blue-50 p-4 rounded-lg mb-6 border border-blue-200 print:hidden">
                <h3 className="text-blue-800 font-bold mb-2">Partial Payment Received</h3>
                <p className="text-blue-700 mb-2">
                  This invoice has been partially paid. Amount due: <span className="font-bold">{formatCurrency(invoice.remainingBalance || 0)}</span>
                </p>
                <button 
                  onClick={() => handleStatusChange('paid')}
                  className="btn btn-primary"
                >
                  Add Payment
                </button>
              </div>
            )}
            
            {/* Payment details for partial payments */}
            {invoice.status === 'partial' && (
              <div className="invoice-payment-details">
                <h2 className="invoice-section-title">Payment Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="invoice-detail-row">
                      <span className="invoice-detail-label">Total Amount:</span> 
                      {formatCurrency(invoice.total || 0)}
                    </p>
                    <p className="invoice-detail-row">
                      <span className="invoice-detail-label">Amount Paid:</span> 
                      {formatCurrency(invoice.amountPaid || 0)}
                    </p>
                    <p className="invoice-detail-row font-bold">
                      <span className="invoice-detail-label">Amount Due:</span> 
                      <span className="text-red-600">{formatCurrency(invoice.remainingBalance || 0)}</span>
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Invoice Items */}
            {invoice.items && invoice.items.length > 0 && (
              <div className="invoice-items">
                <h2 className="invoice-section-title">Items</h2>
                <div className="overflow-x-auto">
                  <div className="invoice-items-header">
                    <div>Description</div>
                    <div className="text-center">Quantity</div>
                    <div className="text-right">Price</div>
                    <div className="text-right">Total</div>
                  </div>
                  
                  {invoice.items.map((item, index) => (
                    <div key={index} className="invoice-item">
                      <div>{item.description}</div>
                      <div className="text-center">{item.quantity}</div>
                      <div className="text-right">{formatCurrency(item.price)}</div>
                      <div className="text-right">{formatCurrency(item.quantity * item.price)}</div>
                    </div>
                  ))}
                  
                  <div className="invoice-total">
                    <div className="flex justify-between mb-2">
                      <span>Subtotal{invoice.vatRate > 0 && !invoice.taxInclusive ? ' (excl. VAT)' : ''}:</span>
                      <span>{formatCurrency(invoice.subtotal || calculateSubtotal())}</span>
                    </div>
                    
                    {invoice.vatRate > 0 && (
                      <div className="flex justify-between mb-2">
                        <span>VAT ({(invoice.vatRate * 100).toFixed(0)}%{invoice.taxInclusive ? ', incl.' : ''}):</span>
                        <span>{formatCurrency(invoice.vatAmount || 0)}</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between font-bold">
                      <span>Total:</span>
                      <span>{formatCurrency(invoice.total || 0)}</span>
                    </div>
                    
                    {invoice.status === 'partial' && (
                      <>
                        <div className="flex justify-between text-green-600 mt-2">
                          <span>Paid:</span>
                          <span>{formatCurrency(invoice.amountPaid || 0)}</span>
                        </div>
                        <div className="flex justify-between text-red-600 font-bold">
                          <span>Due:</span>
                          <span>{formatCurrency(invoice.remainingBalance || 0)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Notes */}
            {invoice.notes && (
              <div className="invoice-notes">
                <h2 className="invoice-section-title">Notes</h2>
                <p>{invoice.notes}</p>
              </div>
            )}
            
            {/* Payment History */}
            {invoice.paymentHistory && invoice.paymentHistory.length > 0 && (
              <div className="invoice-payment-history">
                <h2 className="invoice-section-title">Payment History</h2>
                <div className="overflow-x-auto">
                  <table className="payment-history-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Receipt #</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.paymentHistory.map((payment, index) => (
                        <tr key={index}>
                          <td data-label="Date">{formatDate(payment.date)}</td>
                          <td data-label="Amount">{formatCurrency(payment.amount)}</td>
                          <td data-label="Method">
                            {payment.method === 'cash' ? 'Cash' : 
                             payment.method === 'bank_transfer' ? 'Bank Transfer' :
                             payment.method === 'credit_card' ? 'Credit Card' :
                             payment.method === 'check' ? 'Check' : 'Other'}
                          </td>
                          <td data-label="Receipt #">
                            {payment.receiptNumber && (
                              <Link to={`/receipts?search=${payment.receiptNumber}`}>
                                {payment.receiptNumber}
                              </Link>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            <div className="text-center text-gray-500 mt-8 print:mt-16">
              Thank you for your business!
            </div>
          </div>
        </div>
        
        {pdfPreview && (
          <div className="pdf-preview-container">
            <h2 className="pdf-preview-title">PDF Preview</h2>
            <iframe
              src={pdfPreview}
              title="PDF Preview"
              width="100%"
              height="600px"
              frameBorder="0"
            ></iframe>
          </div>
        )}
      </div>
      {/* Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onSubmit={handlePayment}
        invoiceTotal={invoice ? invoice.total : 0}
        formatCurrency={formatCurrency}
      />
      
      {/* Receipt Confirmation Modal */}
      <ConfirmationModal
        isOpen={isReceiptModalOpen}
        onClose={() => setIsReceiptModalOpen(false)}
        onConfirm={handleViewReceipt}
        title="Receipt Generated"
        message="Payment has been processed and a receipt has been generated. Would you like to view it now?"
      />
    </div>
  );
}
